'use strict';

import * as THREE from 'three';
import { Color } from 'three';

import { SkyviewAssetPositionProps } from '../../react/components/skyview/tooltip/skyview-asset-position-tooltip';
import { MetaDefinition } from '../../services/metadata/meta-types';
import { FeatureFlagsManager } from '../../utils/core/feature-flags-manager';
import { isDefined } from '../../utils/variables';
import cameraManager from '../camera-manager';
import { CoordinateManager } from '../coordinate-manager';
import { Conic } from '../elements/design/design-node/utils/conic';
import { InputHandler } from '../input-handler';
import { publish, SubscriptionTopic } from '../messaging/pubsub';
import {
  KeyInputData,
  MouseButtonType,
  MouseInputData,
  TouchInputData,
} from '../utilities/input-event/input-event-data-extractor';
import { clamp } from '../utilities/math-helpers';
import { Pick, setRayCasterParamsBasedOnCamera } from '../utilities/pick';
import { PickingSphere } from './picking-sphere';
import { Tool } from './tool';
import { buildTooltipProps, MenuTooltipTool, showToolTooltip } from './tooltip-helper';
import { AssetSnapping } from './utils/asset-snapping';
import { copyCoordinate } from './utils/context-menu/commands';
import { HeightLayersGuide } from './utils/height-layers';
import { CenterCurveHighlight } from './utils/snap-candidates';

export class PoiTool extends Tool {
  tooltipCallback?: (tooltip?: SkyviewAssetPositionProps) => void = undefined;
  /**
   * Set while the tooltip is shown in the snapping dialog, which then gets it instead of the cursor.
   */
  onMenuTooltipChanged?: MenuTooltipTool['onMenuTooltipChanged'];

  private point: THREE.Vector3 | undefined;

  private cursor: PickingSphere;

  /**
   * The snapping set in the snapping dialog, with the flag on. Assets are snapped to, like before.
   */
  snapping = new AssetSnapping({ assets: true });
  private centerCurves?: Conic[];
  private centerHighlight = new CenterCurveHighlight();
  /**
   * All heights at the snapped point, while its option is on.
   */
  readonly heightsGuide = new HeightLayersGuide();

  private xCoord: number | undefined;
  private yCoord: number | undefined;
  private zCoord: number | undefined;

  onPoiPlaced?: (point: THREE.Vector3) => void;

  constructor(inputHandler: InputHandler) {
    super('poi', inputHandler);

    this.visible = false;

    this.cursor = new PickingSphere(new Color(0xffff00));
    this.add(this.cursor);
    this.add(this.centerHighlight);
    this.add(this.heightsGuide);
  }

  get snappingOptions() {
    return FeatureFlagsManager.instance.isEnabled('SK-7558-measure-snapping-options');
  }

  onTooltipChanged(callback: (tooltip?: SkyviewAssetPositionProps) => void) {
    this.tooltipCallback = callback;
  }

  getMeta(sweref: string): MetaDefinition {
    return {
      src: 'tool.poi',
      parameters: undefined,
    };
  }

  // OBS WARN Touch är INTE implementerat med all funktionalitet.
  onTouchStart(e: TouchInputData) {
    return this.onTouchMove(e);
  }

  onTouchMove(e: TouchInputData) {
    this.placePointer(e.touches[0].position.x, e.touches[0].position.y);

    return true;
  }

  onMouseOut(e: MouseInputData) {
    this.clearTooltip();
    return true;
  }

  removePointer() {
    this.position.copy(new THREE.Vector3(-1000, -1000, -1000));
  }

  getIntersectionPoint(x: number, y: number) {
    const camera = cameraManager().getActiveCamera();

    if (!camera) {
      return;
    }

    const pixelSnapThreshold = 20;
    const minPickingDistance = 0.1;
    const maxPickingDistance = 350;

    const rayCaster = camera.getRay(x, y);
    setRayCasterParamsBasedOnCamera(
      camera.getTHREECamera(),
      rayCaster,
      pixelSnapThreshold,
      clamp(
        this.cursor.position.distanceTo(rayCaster.ray.origin),
        minPickingDistance,
        maxPickingDistance,
      ),
    );

    if (this.snappingOptions) {
      const { point, curves } = this.snapping.findSnapPoint(camera, rayCaster, {
        fallbackHeight: 0,
      });
      this.centerCurves = curves;
      return point;
    }

    return Pick.instance.closestPoint(camera, rayCaster) ?? Pick.instance.height(x, y, 0);
  }

  /**
   * Moves the cursor to where a POI would be placed, and returns it. There is none where nothing is
   * snapped to, e.g. away from the points of the snap styles.
   */
  placePointer(x: number, y: number) {
    const intersectionPoint = this.getIntersectionPoint(x, y);
    this.cursor.visible = isDefined(intersectionPoint);
    this.centerHighlight.show(intersectionPoint && this.centerCurves);

    if (!intersectionPoint) {
      this.heightsGuide.hide();
      this.clearTooltip();
      return undefined;
    }

    this.position.copy(intersectionPoint);
    // In world coordinates, although the tool follows the cursor.
    this.centerHighlight.position.copy(this.position).negate();
    this.heightsGuide.update(cameraManager().getActiveCamera()!, intersectionPoint);

    const projectionorigo = CoordinateManager.getInstance().getProjectionOrigo()!;

    this.xCoord = intersectionPoint.x + (projectionorigo.x || 0);
    this.yCoord = intersectionPoint.y + (projectionorigo.y || 0);
    this.zCoord = intersectionPoint.z + (projectionorigo.z || 0);

    publish(SubscriptionTopic.ViewerCursorPositionChanged, {
      easting: this.xCoord,
      northing: this.yCoord,
      height: this.zCoord,
    });
    this.updateTooltip(intersectionPoint);

    return this.position.clone();
  }

  onMouseWheel(e: MouseInputData) {
    const camera = cameraManager().getActiveCamera();

    if (!isDefined(camera)) {
      return true;
    }

    this.placePointer(e.pointer.position.x, e.pointer.position.y);

    return true;
  }

  onKeyDown(input: KeyInputData) {
    this.inputHandler.handleKeyPress(
      input,
      'c',
      () => {
        if (isDefined(this.xCoord) && isDefined(this.yCoord) && isDefined(this.zCoord)) {
          void copyCoordinate({ x: this.xCoord, y: this.yCoord, z: this.zCoord }).action();
        }
      },
      'Control',
    );
  }

  onMouseMove(e: MouseInputData) {
    const camera = cameraManager().getActiveCamera();
    if (!isDefined(camera)) {
      return true;
    }

    this.placePointer(e.pointer.position.x, e.pointer.position.y);
    return true;
  }

  onMouseDown(e: MouseInputData) {
    this.mousePosition = e.pointer.client;

    return true;
  }

  async onMouseUp(e: MouseInputData) {
    if (e.button.type === MouseButtonType.RightButton) {
      if (this.withinThreshold(e.pointer.client.x, e.pointer.client.y)) {
        this.inputHandler.setTool('select');
      }
      return Promise.resolve(true);
    }

    if (this.withinThreshold(e.pointer.client.x, e.pointer.client.y)) {
      const point = this.placePointer(e.pointer.position.x, e.pointer.position.y);

      if (point) {
        this.point = point;
        this.onPoiPlaced?.(point);
      }
    }

    return Promise.resolve(true);
  }

  enable() {
    this.visible = true;
    this.cursor.visible = true;
    this.inputHandler.setCursor('crosshair');
  }

  async disable() {
    this.visible = false;
    this.centerHighlight.show(undefined);
    this.heightsGuide.hide();
    this.inputHandler.setCursor('');
    this.point = undefined;
    publish(SubscriptionTopic.ViewerCursorPositionChanged, null);
    this.clearTooltip();
    return Promise.resolve();
  }

  updateTooltip(point: THREE.Vector3) {
    const camera = cameraManager().getActiveCamera();
    const canUpdatePlaceTooltip = isDefined(point) && isDefined(camera);
    if (!canUpdatePlaceTooltip) {
      return;
    }

    const screenPoint = camera.getScreenPosition(point);

    showToolTooltip(this, {
      ...buildTooltipProps(
        undefined,
        camera,
        {
          easting: this.xCoord!,
          northing: this.yCoord!,
          height: this.zCoord!,
        },
        {
          x: screenPoint.x,
          y: screenPoint.y,
        },
      ),
      heights: this.heightsGuide.getTooltipHeights(point),
    });
  }

  clearTooltip() {
    publish(SubscriptionTopic.ViewerCursorPositionChanged, null);
    showToolTooltip(this);
  }
}
